import type { Camera, Vec2 } from '@/types';
import { screenToWorld, clampZoom } from '@/core/coordinates/coordinateSystem';

/** Two touch points in screen space. */
export interface PinchPoints {
  readonly a: Vec2;
  readonly b: Vec2;
}

const midpoint = (p: PinchPoints): Vec2 => ({ x: (p.a.x + p.b.x) / 2, y: (p.a.y + p.b.y) / 2 });
const distance = (p: PinchPoints): number => Math.hypot(p.a.x - p.b.x, p.a.y - p.b.y);

/**
 * Camera after a two-finger gesture moves from `prev` to `next`. The world point
 * under the previous midpoint ends up under the new midpoint, and zoom scales by
 * the change in finger distance. Pure and independent of the input source.
 */
export const applyPinch = (camera: Camera, prev: PinchPoints, next: PinchPoints): Camera => {
  const d0 = distance(prev);
  const d1 = distance(next);
  const zoom = d0 > 0 ? clampZoom(camera.zoom * (d1 / d0)) : camera.zoom;
  const anchorWorld = screenToWorld(midpoint(prev), camera);
  const target = midpoint(next);
  return {
    zoom,
    position: { x: anchorWorld.x - target.x / zoom, y: anchorWorld.y - target.y / zoom },
  };
};
